import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { VehiclePricing, VehiclePricingAddon } from '@limo/database'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PricingTypeCard } from './PricingTypeCard'
import { VehicleAddonDialog } from './VehicleAddonDialog'
import { useToast } from '@/hooks/use-toast'
import { DollarSign, AlertTriangle } from 'lucide-react'

type VehiclePricingWithAddons = VehiclePricing & {
  vehicle_pricing_addons: VehiclePricingAddon[]
}

interface VehiclePricingSectionProps {
  vehicleId: string
  accountId: string
  pricing: VehiclePricingWithAddons[]
  onRefresh: () => void
}

export function VehiclePricingSection({
  vehicleId,
  accountId,
  pricing,
  onRefresh,
}: VehiclePricingSectionProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Addon dialog state
  const [addonDialogOpen, setAddonDialogOpen] = useState(false)
  const [editAddonId, setEditAddonId] = useState<string | null>(null)
  const [addonPricingId, setAddonPricingId] = useState<string | null>(null)

  // Delete addon state
  const [deleteAddonId, setDeleteAddonId] = useState<string | null>(null)

  // Find pricing for each type
  const pointToPointPricing = pricing.find((p) => p.pricing_type === 'point_to_point') || null
  const hourlyPricing = pricing.find((p) => p.pricing_type === 'hourly') || null

  // Check if all pricing is disabled (either doesn't exist or is_active is false)
  const hasActivePricing =
    (pointToPointPricing?.is_active ?? false) || (hourlyPricing?.is_active ?? false)

  // Delete addon mutation
  const deleteAddonMutation = useMutation({
    mutationFn: async (addonId: string) => {
      const { error } = await supabase
        .from('vehicle_pricing_addons')
        .delete()
        .eq('id', addonId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
      toast({ title: 'Add-on deleted' })
      setDeleteAddonId(null)
      onRefresh()
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const handleEditAddon = (pricingId: string, addonId: string | null) => {
    setAddonPricingId(pricingId)
    setEditAddonId(addonId)
    if (!addonId) {
      setAddonDialogOpen(true)
    }
  }

  const handleDeleteAddon = (addonId: string) => {
    setDeleteAddonId(addonId)
  }

  const handleAddonDialogChange = (open: boolean) => {
    setAddonDialogOpen(open)
    if (!open) {
      setEditAddonId(null)
      setAddonPricingId(null)
    }
    queryClient.invalidateQueries({ queryKey: ['vehicle-detail', vehicleId] })
    onRefresh()
  }

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            <CardTitle>Pricing</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Warning banner when all pricing is disabled */}
          {!hasActivePricing && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This vehicle will not appear in the booking flow until at least one pricing
                type is enabled.
              </AlertDescription>
            </Alert>
          )}

          {/* Pricing Type Cards */}
          <div className="space-y-4">
            <PricingTypeCard
              pricingType="point_to_point"
              vehicleId={vehicleId}
              accountId={accountId}
              pricing={pointToPointPricing}
              onPricingChange={onRefresh}
              onEditAddon={handleEditAddon}
              onDeleteAddon={handleDeleteAddon}
            />
            <PricingTypeCard
              pricingType="hourly"
              vehicleId={vehicleId}
              accountId={accountId}
              pricing={hourlyPricing}
              onPricingChange={onRefresh}
              onEditAddon={handleEditAddon}
              onDeleteAddon={handleDeleteAddon}
            />
          </div>
        </CardContent>
      </Card>

      {/* Addon Dialog */}
      {addonPricingId && (
        <VehicleAddonDialog
          addonId={editAddonId}
          pricingId={addonPricingId}
          accountId={accountId}
          open={addonDialogOpen || !!editAddonId}
          onOpenChange={handleAddonDialogChange}
        />
      )}

      {/* Delete Addon Confirmation */}
      <AlertDialog open={!!deleteAddonId} onOpenChange={() => setDeleteAddonId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Add-on</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this add-on? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteAddonId && deleteAddonMutation.mutate(deleteAddonId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
